/**
 * PackReceiptPanel.jsx — RC 1.47.P1.5 (2026-05-03).
 *
 * Read-only receipt + provenance card for the Application Pack
 * detail page. Surfaces the operator-grade "what was selected,
 * what was assessed, and what was charged" attestation using
 * data already persisted by the platform — no new endpoints, no
 * write actions, no LLM round-trips.
 *
 * Visibility rules:
 *   • Collapsible — closed by default to keep the Overview tab
 *     clean for the 80% who don't audit.
 *   • The "Prep based on:" provenance line stays visible regardless
 *     of collapsed state so the user never loses sight of which CV
 *     the entire receipt refers to.
 *   • Empty values render as a muted "—" (not hidden) so absence
 *     is visually honest.
 *
 * Copy — operator-locked verbatim:
 *   • Card header:  "Receipt & provenance"
 *   • Subline:      "What was selected, what was assessed, and what
 *                    was charged for this pack."
 *   • Footer:       "Read-only attestation — does not trigger a
 *                    re-grade or charge."
 *   • Provenance:   "Prep based on:" + binding.cv_label
 *   • Verdict:      "Preview — finalize to confirm" / "Source CV grade"
 *   • Charge:       "N Credits — quality uplift"    (N from creditsContext)
 *                   "N Credits — presentation optimisation" (RC 1.48.6)
 *                   "Waived — within ±5 tolerance band"
 *                   "Waived — variant did not improve"
 *                   "Not finalized"
 *   • Rubric stale: badge only — no actionable refresh link
 *                   (RC 1.49.x.PACK-FINALITY 2026-05-06)
 */

import { useState } from 'react';
import PropTypes from 'prop-types';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Receipt, ChevronDown, ChevronUp, ShieldCheck, RefreshCcw } from 'lucide-react';
import { useCredits } from '@/lib/creditsContext';
import { getActionCost } from '@/lib/creditsHelpers';

const EM_DASH = '\u2014';
const PLUS_MINUS = '\u00B1';

function fmtPct(raw) {
  if (raw === null || raw === undefined) return null;
  const n = typeof raw === 'number' ? raw : Number(raw);
  if (!Number.isFinite(n)) return null;
  const scaled = n <= 1 ? Math.round(n * 100) : Math.round(n);
  return Math.min(100, Math.max(0, scaled));
}

function fmtDelta(raw) {
  if (raw === null || raw === undefined) return null;
  const n = typeof raw === 'number' ? raw : Number(raw);
  if (!Number.isFinite(n)) return null;
  if (n > 0) return `+${Math.round(n)}`;
  if (n < 0) return `\u2212${Math.abs(Math.round(n))}`;   // U+2212 minus sign
  return `${PLUS_MINUS}0`;
}

function fmtIso(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function humaniseDecision(d) {
  if (!d) return null;
  const map = {
    shortlist:                  'Shortlist',
    apply_with_repositioning:   'Apply with repositioning',
    hold:                       'Hold',
    skip:                       'Skip',
  };
  return map[d] || d;
}

function chargeOutcome(variant, binding, cvImproveCost) {
  // No variant at all = pack is Source-CV-only. There is no finalize-
  // charge to report; the receipt still surfaces "Not finalized" as
  // the honest state.
  if (!variant) return { label: 'Not finalized', tone: 'muted' };
  const reason = variant.charge_reason;
  if (variant.charged_at) {
    if (reason === 'cv_quality_improved') {
      // RC 1.48.7B — read the live `cv_improve` Credit cost from
      // creditsContext instead of the pre-RC 1.48.7B hardcoded
      // "9 Credits" literal. Falls back to an unlabelled "Quality
      // uplift" if the context hasn't resolved yet so the receipt
      // is never rendered with a stale number.
      if (cvImproveCost !== null && cvImproveCost !== undefined) {
        const unit = cvImproveCost === 1 ? 'Credit' : 'Credits';
        return { label: `${cvImproveCost} ${unit} — quality uplift`, tone: 'success' };
      }
      return { label: 'Quality uplift', tone: 'success' };
    }
    // RC 1.48.6 — presentation-optimisation high-CV branch.
    if (reason === 'presentation_optimisation_high_cv') {
      // 50% of cv_improve. `max(1, round(N/2))` mirrors backend.
      if (cvImproveCost !== null && cvImproveCost !== undefined) {
        const half = Math.max(1, Math.round(cvImproveCost / 2));
        const unit = half === 1 ? 'Credit' : 'Credits';
        return { label: `${half} ${unit} — presentation optimisation`, tone: 'info' };
      }
      return { label: 'Presentation optimisation', tone: 'info' };
    }
    if (reason === 'within_tolerance_band')
      return { label: 'Waived — within \u00B15 tolerance band', tone: 'info' };
    if (reason === 'regression' || reason === 'cv_quality_no_improvement')
      return { label: 'Waived — variant did not improve', tone: 'muted' };
    return { label: reason || 'Charged', tone: 'muted' };
  }
  // Variant exists but never finalize-charged. Pack is bound to a
  // preview variant.
  if (binding?.cv_kind === 'tailored_variant')
    return { label: 'Not finalized', tone: 'muted' };
  return { label: 'Not finalized', tone: 'muted' };
}

function verdictStatus(canonicalFit, variant, binding) {
  if (variant?.charged_at) return { label: 'Finalized', tone: 'success' };
  if (canonicalFit?.cv_sub_score_provisional)
    return { label: 'Preview — finalize to confirm', tone: 'warning' };
  if (binding?.cv_kind === 'source_cv')
    return { label: 'Source CV grade', tone: 'info' };
  return { label: '—', tone: 'muted' };
}

const TONE_CLASS = {
  success: 'bg-ndor-success-light text-ndor-success border-ndor-success/20',
  info:    'bg-ndor-info-light    text-ndor-info    border-ndor-info/20',
  warning: 'bg-ndor-warning-light text-ndor-warning border-ndor-warning/30',
  muted:   'bg-ndor-surface-alt   text-ndor-ink-muted border-ndor-edge',
};

function Row({ k, v, testId }) {
  return (
    <div
      className="flex items-baseline justify-between gap-3 py-1.5 border-b border-ndor-edge last:border-b-0"
      data-testid={`pack-receipt-row-${testId}`}
    >
      <dt className="text-[11px] uppercase tracking-[0.14em] text-ndor-ink-muted font-mono-ui shrink-0">
        {k}
      </dt>
      <dd className="text-sm text-ndor-ink text-right">
        {v === null || v === undefined || v === ''
          ? <span className="text-ndor-ink-muted">{EM_DASH}</span>
          : v}
      </dd>
    </div>
  );
}
Row.propTypes = {
  k:      PropTypes.string.isRequired,
  v:      PropTypes.node,
  testId: PropTypes.string.isRequired,
};

function ToneBadge({ status }) {
  return (
    <Badge
      variant="outline"
      className={`text-[10px] ${TONE_CLASS[status.tone] || TONE_CLASS.muted}`}
    >
      {status.label}
    </Badge>
  );
}
ToneBadge.propTypes = {
  status: PropTypes.shape({
    label: PropTypes.string.isRequired,
    tone:  PropTypes.string.isRequired,
  }).isRequired,
};

export default function PackReceiptPanel({ pack }) {
  const [expanded, setExpanded] = useState(false);
  // RC 1.48.7B — live `cv_improve` price from creditsContext. Used
  // by `chargeOutcome()` to render the honest Credit amount on the
  // receipt without any hardcoded "9 Credits" literal.
  const { usageCosts } = useCredits();
  const cvImproveCost = getActionCost(usageCosts, 'cv_improve');

  if (!pack) return null;
  const binding   = pack.binding || {};
  const cf        = pack.canonical_fit || null;
  const variant   = pack.cv_variant || null;
  const baseCv    = pack.base_cv || null;
  const letter    = pack.cover_letter || null;
  const opt       = (variant && variant.optimisation_artifacts) || null;
  const meta      = (opt && opt.analytics_metadata) || null;

  const verdict   = verdictStatus(cf, variant, binding);
  const charge    = chargeOutcome(variant, binding, cvImproveCost);
  const rubricStale = cf?.rubric_status === 'stale_refresh_available';

  return (
    <Card
      className="border-ndor-edge shadow-sm"
      data-testid="pack-receipt-panel"
    >
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="text-sm font-medium text-ndor-ink flex items-center gap-2">
              <Receipt className="h-4 w-4 text-ndor-ink-muted" />
              Receipt &amp; provenance
            </CardTitle>
            <p className="text-[11px] text-ndor-ink-muted mt-1">
              What was selected, what was assessed, and what was charged for this pack.
            </p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setExpanded((v) => !v)}
            className="text-xs text-ndor-info shrink-0"
            data-testid="pack-receipt-toggle"
          >
            {expanded ? (
              <><ChevronUp className="h-3.5 w-3.5 mr-1" /> Hide details</>
            ) : (
              <><ChevronDown className="h-3.5 w-3.5 mr-1" /> Show details</>
            )}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Always-visible provenance line */}
        <div
          className="flex flex-wrap items-baseline gap-2 text-sm pt-1"
          data-testid="pack-receipt-provenance"
        >
          <span className="text-[11px] uppercase tracking-[0.14em] text-ndor-ink-muted font-mono-ui">
            Prep based on:
          </span>
          <span className="text-ndor-ink font-medium">
            {binding?.cv_label || EM_DASH}
          </span>
          <ToneBadge status={verdict} />
        </div>

        {expanded && (
          <div className="space-y-4 pt-1" data-testid="pack-receipt-detail">
            {/* Selected documents */}
            <section>
              <h4 className="text-[11px] uppercase tracking-[0.14em] text-ndor-ink-muted font-mono-ui mb-1">
                Selected documents
              </h4>
              <dl>
                <Row
                  k="Source CV"
                  v={baseCv?.original_filename}
                  testId="source-cv"
                />
                <Row
                  k="Tailored Variant"
                  v={variant?.variant_name || (binding?.cv_kind === 'tailored_variant' ? binding?.cv_label : null)}
                  testId="tailored-variant"
                />
                <Row
                  k="Cover letter"
                  v={letter?.title || (letter ? null : 'Not included')}
                  testId="cover-letter"
                />
              </dl>
            </section>

            {/* Canonical fit */}
            <section>
              <h4 className="text-[11px] uppercase tracking-[0.14em] text-ndor-ink-muted font-mono-ui mb-1">
                Canonical fit
              </h4>
              <dl>
                <Row
                  k="NDOR fit score"
                  v={fmtPct(cf?.fit_score)}
                  testId="fit-score"
                />
                <Row
                  k="NDOR CV verdict"
                  v={cf?.cv_sub_score}
                  testId="cv-verdict"
                />
                <Row
                  k="Verdict status"
                  v={<ToneBadge status={verdict} />}
                  testId="verdict-status"
                />
                <Row
                  k="Final decision"
                  v={humaniseDecision(cf?.final_decision)}
                  testId="final-decision"
                />
                <Row
                  k="Evaluated"
                  v={fmtIso(cf?.evaluated_at)}
                  testId="evaluated"
                />
              </dl>
            </section>

            {/* Charge */}
            <section>
              <h4 className="text-[11px] uppercase tracking-[0.14em] text-ndor-ink-muted font-mono-ui mb-1">
                Charge
              </h4>
              <dl>
                <Row
                  k="Charge outcome"
                  v={<ToneBadge status={charge} />}
                  testId="charge-outcome"
                />
                <Row
                  k="Δ vs Source"
                  v={fmtDelta(meta?.variant_uplift_delta)}
                  testId="delta"
                />
                <Row
                  k="Source CV verdict at finalize"
                  v={meta?.source_cv_sub_score}
                  testId="source-cv-sub-score"
                />
                <Row
                  k="Variant CV verdict at finalize"
                  v={variant?.cv_sub_score_at_finalize ?? meta?.variant_cv_sub_score}
                  testId="variant-cv-sub-score"
                />
                <Row
                  k="Tolerance band"
                  v={`${PLUS_MINUS}5 points`}
                  testId="tolerance-band"
                />
                <Row
                  k="Finalized at"
                  v={fmtIso(variant?.charged_at)}
                  testId="finalized-at"
                />
              </dl>
            </section>

            {/* Build attestation */}
            <section>
              <h4 className="text-[11px] uppercase tracking-[0.14em] text-ndor-ink-muted font-mono-ui mb-1">
                Build attestation
              </h4>
              <dl>
                <Row
                  k="Rubric version"
                  v={cf?.rubric_version}
                  testId="rubric-version"
                />
                <Row
                  k="Scoring model"
                  v={cf?.scoring_model_version}
                  testId="scoring-model-version"
                />
                <Row
                  k="Build label"
                  v={pack.current_version}
                  testId="current-version"
                />
                <Row
                  k="Rubric status"
                  v={
                    rubricStale ? (
                      <span className="inline-flex items-center gap-2">
                        <Badge
                          variant="outline"
                          className={`text-[10px] ${TONE_CLASS.warning}`}
                        >
                          <RefreshCcw className="h-3 w-3 mr-1" />
                          Rubric updated since this score
                        </Badge>
                      </span>
                    ) : 'Current'
                  }
                  testId="rubric-status"
                />
              </dl>
            </section>

            {/* Defensive footer */}
            <p
              className="text-[11px] italic text-ndor-ink-muted flex items-center gap-1.5 pt-1"
              data-testid="pack-receipt-footer"
            >
              <ShieldCheck className="h-3 w-3" />
              Read-only attestation — does not trigger a re-grade or charge.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

PackReceiptPanel.propTypes = {
  pack: PropTypes.object,
};

PackReceiptPanel.defaultProps = {
  pack: null,
};
